import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, type RefetchOptions } from '@tanstack/react-query';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import {
  sessionScopeKey,
  useSessionScopeFence,
  type SessionScopeIdentity,
} from '@/hooks/useSessionScope';
import type { RequestOptions } from '@/services/apiClient';
import { aiSettingsApi } from './aiSettings.api';
import type { AiConfigurationInput } from './aiSettings.types';

export const aiSettingsKeys = {
  all: ['ai-settings'] as const,
  scoped: (kind: string, identity: SessionScopeIdentity) =>
    ['ai-settings', kind, ...sessionScopeKey(identity)] as const,
};
function useAiSettingsRead<T>(
  kind: string,
  permission: string,
  loader: (options: RequestOptions) => Promise<T>,
) {
  const { identity, enabled, isCurrent } = useSessionScopeFence(permission);
  const query = useQuery({
    queryKey: aiSettingsKeys.scoped(kind, identity),
    enabled,
    placeholderData: () => undefined,
    queryFn: async ({ signal }) => {
      if (!enabled || !isCurrent(identity))
        throw new Error('Current authenticated access is required.');
      const result = await loader({ signal, sessionOrigin: identity.sessionOrigin });
      signal.throwIfAborted();
      if (!isCurrent(identity)) throw new Error('AI settings access changed during loading.');
      return result;
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchInterval: 60_000,
    retry: false,
  });
  const runRefetch = query.refetch;
  const refetch = useCallback(
    async (options?: RefetchOptions) => {
      if (!enabled || !isCurrent(identity))
        throw new Error('Current authenticated access is required.');
      const result = await runRefetch(options);
      if (!isCurrent(identity)) throw new Error('AI settings access changed during refresh.');
      return { ...result, data: result.isError ? undefined : result.data };
    },
    [enabled, identity, isCurrent, runRefetch],
  );
  return { ...query, refetch, data: enabled && !query.isError ? query.data : undefined };
}
function useAiSettingsMutation<TInput, TOutput>(
  kind: string,
  permission: string,
  action: (input: TInput, options: RequestOptions) => Promise<TOutput>,
  scrub?: (input: TInput) => void,
) {
  const { identity, enabled, isCurrent } = useSessionScopeFence(permission);
  const client = useQueryClient();
  const mutation = useMutation({
    mutationKey: ['ai-settings-mutation', kind, ...sessionScopeKey(identity)],
    gcTime: 0,
    retry: false,
    onMutate: () => identity,
    mutationFn: async (input: TInput) => {
      try {
        if (!enabled || !isCurrent(identity))
          throw new Error('Access changed. Reload before saving AI settings.');
        const result = await action(input, {
          sessionOrigin: identity.sessionOrigin,
          allowRefresh: false,
        });
        if (!isCurrent(identity))
          throw new Error('Your session changed. Reload to check the save outcome.');
        return result;
      } catch (error) {
        if (!isCurrent(identity)) {
          // eslint-disable-next-line preserve-caught-error -- Prior-session errors must not escape through Error.cause after access changes.
          throw new Error('Your session changed. Reload to check the save outcome.');
        }
        throw error;
      } finally {
        scrub?.(input);
      }
    },
    onSuccess: async (_data, _input, origin) => {
      if (!origin || !isCurrent(origin)) return;
      await client.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'ai-settings' &&
          sessionScopeKey(origin).every((part, index) => query.queryKey[index + 2] === part),
      });
      await client.invalidateQueries({ queryKey: ['ai', 'status'] });
    },
  });
  const visible = mutation.context && isCurrent(mutation.context);
  return {
    ...mutation,
    data: visible ? mutation.data : undefined,
    error: visible ? mutation.error : null,
    isPending: Boolean(visible && mutation.isPending),
  };
}
function usePreferencePermission(): string {
  const { permissions } = useAuth();
  return hasPermission(permissions, PERMISSIONS.AI_CHAT_READ)
    ? PERMISSIONS.AI_CHAT_READ
    : PERMISSIONS.SYSTEM_AI_PROVIDERS_READ;
}
export function useAiConfiguration() {
  return useAiSettingsRead(
    'configuration',
    PERMISSIONS.SYSTEM_AI_PROVIDERS_READ,
    aiSettingsApi.getConfiguration,
  );
}
export function useAiAssistantPreferences() {
  return useAiSettingsRead('preferences', usePreferencePermission(), aiSettingsApi.getPreferences);
}
export function useAiKnowledge() {
  return useAiSettingsRead(
    'knowledge',
    PERMISSIONS.SYSTEM_AI_PROVIDERS_READ,
    aiSettingsApi.getKnowledge,
  );
}
export function useAiGuardrails() {
  return useAiSettingsRead(
    'guardrails',
    PERMISSIONS.SYSTEM_AI_PROVIDERS_READ,
    aiSettingsApi.getGuardrails,
  );
}
export function useSaveAiPreferences() {
  return useAiSettingsMutation(
    'preferences',
    usePreferencePermission(),
    aiSettingsApi.savePreferences,
  );
}
export function useSaveAiConfiguration() {
  return useAiSettingsMutation(
    'configuration',
    PERMISSIONS.SETTINGS_MANAGE,
    aiSettingsApi.saveConfiguration,
    (input: AiConfigurationInput) => {
      if (input.providers.anthropic) delete input.providers.anthropic.apiKey;
      if (input.providers.openai) delete input.providers.openai.apiKey;
    },
  );
}
export function useAiConnectionTest() {
  return useAiSettingsMutation(
    'connection-test',
    PERMISSIONS.SETTINGS_MANAGE,
    aiSettingsApi.testConnection,
  );
}
export function useAiKnowledgeMutations() {
  return {
    create: useAiSettingsMutation(
      'knowledge-create',
      PERMISSIONS.SETTINGS_MANAGE,
      aiSettingsApi.createKnowledge,
    ),
    remove: useAiSettingsMutation(
      'knowledge-delete',
      PERMISSIONS.SETTINGS_MANAGE,
      aiSettingsApi.deleteKnowledge,
    ),
  };
}
export function useAiGuardrailMutations() {
  return {
    updatePolicy: useAiSettingsMutation(
      'guardrail-policy',
      PERMISSIONS.SETTINGS_MANAGE,
      aiSettingsApi.saveGuardrails,
    ),
    createRule: useAiSettingsMutation(
      'rule-create',
      PERMISSIONS.SETTINGS_MANAGE,
      aiSettingsApi.createRule,
    ),
    updateRule: useAiSettingsMutation(
      'rule-update',
      PERMISSIONS.SETTINGS_MANAGE,
      aiSettingsApi.updateRule,
    ),
    removeRule: useAiSettingsMutation(
      'rule-delete',
      PERMISSIONS.SETTINGS_MANAGE,
      aiSettingsApi.deleteRule,
    ),
  };
}
